'use client';

import { MagnifyingGlass, XCircle } from '@phosphor-icons/react/ssr';
import {
  type ComponentProps,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import { tv } from '../../lib/tv';
import { Input } from '../ui/input';
import { useFieldControl } from './field';
import { Kbd } from './kbd';
import { textInputStyles } from './text-input';

export type SearchFieldProps = Omit<
  ComponentProps<'input'>,
  'size' | 'value' | 'defaultValue' | 'onChange' | 'type'
> & {
  /** Figma "Size": Medium 40 (default) · Large 48. */
  size?: 'md' | 'lg';
  /** The query (controlled). */
  value?: string;
  /** Initial query when uncontrolled. */
  defaultValue?: string;
  /** Called on every keystroke and on clear. */
  onValueChange?: (value: string) => void;
  /** Called with the query once typing pauses for `debounce` ms (and at once on clear). */
  onSearch?: (value: string) => void;
  /** Delay for `onSearch`, in ms. Figma: 150. */
  debounce?: number;
  /**
   * Figma "Show shortcut": the key that, with ⌘ (or Ctrl), focuses the field
   * from anywhere on the page. Shown as a ⌘K hint at rest. `false` turns the
   * shortcut and hint off.
   */
  shortcut?: string | false;
  /** Class for the outer box (width, margins). */
  className?: string;
};

/**
 * Figma Search Field (91:11023): the Text Input box (radius/md at Medium,
 * radius/lg at Large, 12 / 16 left, 8 right, gap 8) with an 18 / 20 icon/secondary search icon.
 * Right side: ⌘K Kbd at rest, Esc Kbd while focused, a 16px icon/tertiary
 * clear button once filled.
 */
const searchField = tv({
  slots: {
    root: 'group/search',
    input: '[&::-webkit-search-cancel-button]:appearance-none',
    icon: 'text-icon-secondary shrink-0',
    // ⌘K at rest, Esc while focused - both by CSS, so forced states render too.
    shortcut: 'flex shrink-0 group-has-[input:focus-visible]/search:hidden',
    escape: 'hidden shrink-0 group-has-[input:focus-visible]/search:flex',
    clear: [
      'text-icon-tertiary flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full outline-none',
      'hover:text-icon-secondary focus-visible:ring-2 focus-visible:ring-border-focus',
    ],
    clearIcon: 'size-4',
  },
  variants: {
    size: {
      md: { root: 'pr-2 pl-3', icon: 'size-[18px]' },
      lg: { root: 'rounded-lg pr-2 pl-4', icon: 'size-5' },
    },
  },
  defaultVariants: { size: 'md' },
});

/**
 * Global or list search (Figma: Components ↳ Search & Combobox ↳ Search
 * Field). A labelled `searchbox`: ⌘K (or Ctrl+K) focuses it from anywhere,
 * Escape clears the query (or leaves the field when empty), and results
 * should update as you type via the debounced `onSearch`. Enter is yours -
 * open the top result from `onKeyDown`. To pick a value from a list use
 * Combobox.
 */
export function SearchField({
  size = 'md',
  value: valueProp,
  defaultValue = '',
  onValueChange,
  onSearch,
  debounce = 150,
  shortcut = 'k',
  placeholder = 'Search',
  className,
  onKeyDown,
  ...props
}: SearchFieldProps) {
  const [valueState, setValueState] = useState(defaultValue);
  const value = valueProp ?? valueState;
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const control = useFieldControl(props);
  const box = textInputStyles({ size });
  const styles = searchField({ size });

  const update = (next: string, immediate = false) => {
    setValueState(next);
    onValueChange?.(next);
    clearTimeout(timer.current);
    if (!onSearch) return;
    if (immediate) onSearch(next);
    else timer.current = setTimeout(() => onSearch(next), debounce);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!shortcut) return;
    const onDocumentKeyDown = (e: globalThis.KeyboardEvent) => {
      // Already handled - e.g. by a Command Palette on the same key, which
      // listens on window in the capture phase and so always wins.
      if (e.defaultPrevented) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === shortcut.toLowerCase()) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    document.addEventListener('keydown', onDocumentKeyDown);
    return () => document.removeEventListener('keydown', onDocumentKeyDown);
  }, [shortcut]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented || e.key !== 'Escape') return;
    // Chromium clears type=search on Escape by itself; do it here for every browser.
    e.preventDefault();
    if (value) update('', true);
    else inputRef.current?.blur();
  };

  const key = shortcut ? shortcut.toUpperCase() : undefined;

  return (
    <div
      data-slot="search-field"
      className={box.root({ className: styles.root({ className }) })}
    >
      <MagnifyingGlass aria-hidden className={styles.icon()} />
      <Input
        ref={inputRef}
        type="search"
        placeholder={placeholder}
        aria-keyshortcuts={key ? `Meta+${key} Control+${key}` : undefined}
        {...control}
        value={value}
        onChange={(e) => update(e.target.value)}
        onKeyDown={handleKeyDown}
        className={styles.input()}
      />
      {value ? (
        <button
          type="button"
          data-slot="search-field-clear"
          aria-label="Clear search"
          className={styles.clear()}
          onClick={() => {
            update('', true);
            inputRef.current?.focus();
          }}
        >
          <XCircle aria-hidden className={styles.clearIcon()} />
        </button>
      ) : (
        // Hints only: the shortcut is exposed on the input as aria-keyshortcuts.
        !control.disabled && (
          <>
            {key && (
              <span
                aria-hidden
                data-slot="search-field-shortcut"
                className={styles.shortcut()}
              >
                <Kbd modifier="command">{key}</Kbd>
              </span>
            )}
            <span aria-hidden data-slot="search-field-escape" className={styles.escape()}>
              <Kbd>Esc</Kbd>
            </span>
          </>
        )
      )}
    </div>
  );
}
